import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  PencilIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { useGiftAuthGate } from '@service/giftStore/components/GiftStoreAuthGate';
import { updateAdditionalRecipient } from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponShareStatus } from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftDeliveryShareStatus } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  giftOrderForStoreDataAtomFamily,
  giftOrderForStoreStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderForStoreHook } from '@shared/api/giftOrder/giftOrder.hook';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useParams } from 'react-router-dom';

import { CouponRow } from '../components/CouponRow';
import { IndividualDeliveryRow } from '../components/IndividualDeliveryRow';
import { MemoModal } from '../components/MemoModal';
import { ShareConfirmModal } from '../components/ShareConfirmModal';
import { ShareGuideModal } from '../components/ShareGuideModal';
import { GiftCouponShareModal } from '../containers/GiftCouponShareModal/GiftCouponShareModal';
import { GiftDeliveryShareModal } from '../containers/GiftDeliveryShareModal/GiftDeliveryShareModal';
import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { useCourtesyEditor } from '../hooks/useCourtesyEditor';
import { useGiftOrderShare } from '../hooks/useGiftOrderShare';

const GiftOrderCouponPage: FC = function GiftOrderCouponPage() {
  const { orderId } = useParams<{ orderId: string }>();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const { storeRole } = useAuth();
  const { requestReauth } = useGiftAuthGate();

  const role = storeRole();
  useGiftOrderForStoreHook(orderId ?? '', role);
  const orderStatus = useRecoilValue(giftOrderForStoreStatusAtomFamily(orderId ?? ''));
  const order = useRecoilValue(giftOrderForStoreDataAtomFamily(orderId ?? ''));

  useEffect(() => {
    setTitle('답례품 전달');
  }, [setTitle]);

  useEffect(() => {
    if (orderStatus === 'error' || (orderStatus === 'success' && !order)) {
      requestReauth();
    }
  }, [orderStatus, order, requestReauth]);

  const courtesy = useCourtesyEditor({
    orderId: orderId ?? '',
    order,
    role,
  });

  const share = useGiftOrderShare({
    orderId: orderId ?? '',
    order,
    role,
    courtesyContent: courtesy.courtesyContent,
  });

  if (
    orderStatus === 'loading' ||
    orderStatus === 'idle' ||
    orderStatus === 'error' ||
    !order
  ) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  let stepNum = 1;

  return (
    <div
      className={`min-h-screen bg-gradient-to-b from-purple-50 to-white pt-4 ${
        share.totalUnshared > 0 ? 'pb-32' : 'pb-8'
      }`}
    >
      {share.hasShareableItems && (
        <>
          {/* STEP: 답례글 확인 */}
          <CourtesySection stepNum={stepNum++} courtesy={courtesy} />

          {/* STEP: 쿠폰형 답례품 전달 */}
          {share.couponList.length > 0 && (
            <CouponSection
              stepNum={stepNum++}
              couponList={share.couponList}
              totalCoupons={share.totalCoupons}
              sharedCoupons={share.sharedCoupons}
              unsharedCoupons={share.unsharedCoupons}
              onShare={share.handleCouponShare}
              onMemo={share.handleOpenMemoCoupon}
              onAdditionalPhoneEdit={async (couponId, phone) => {
                await updateAdditionalRecipient(couponId, phone, role);
              }}
            />
          )}

          {/* STEP: Individual 배송형 답례품 전달 */}
          {share.isIndividualDelivery && share.deliveryList.length > 0 && (
            <IndividualDeliverySection
              stepNum={stepNum++}
              deliveryList={share.deliveryList}
              totalDeliveries={share.totalIndividualDeliveries}
              sharedDeliveries={share.sharedDeliveries}
              unsharedDeliveries={share.unsharedDeliveries}
              onShare={share.handleDeliveryShare}
              onMemo={share.handleOpenMemoDelivery}
            />
          )}
        </>
      )}

      {/* Bulk 배송형 상품은 배송현황보기에서 확인하므로 여기서는 표시하지 않음 */}

      {!share.hasShareableItems && share.deliveryItems.length === 0 && (
        <div className="px-4 pt-8 text-center">
          <p className="text-sm text-gray-500">주문 상세 정보가 없습니다.</p>
        </div>
      )}

      {/* 하단 고정 전달 버튼 */}
      {share.totalUnshared > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white px-4 py-2">
          <button
            onClick={share.handleBottomButtonClick}
            className="w-full rounded-xl bg-purple-600 py-4 text-lg font-bold text-white active:bg-purple-700"
          >
            다음 미전달 답례품 전달하기 (미전달 {share.totalUnshared}건)
          </button>
        </div>
      )}

      <ShareGuideModal
        open={share.showShareGuide}
        onConfirm={share.handleShareGuideConfirm}
        onClose={() => share.setShowShareGuide(false)}
      />

      <GiftCouponShareModal
        openId={share.couponShareModalOpenId}
        shareKey={share.couponShareOpenCount}
        courtesyContent={courtesy.courtesyContent}
        linkUrl={share.currentCouponShareUrl}
        seq={share.currentShareSeq}
        onShare={share.handleCouponShareComplete}
      />

      <GiftDeliveryShareModal
        openId={share.deliveryShareModalOpenId}
        shareKey={share.deliveryShareOpenCount}
        courtesyContent={courtesy.courtesyContent}
        linkUrl={share.currentDeliveryShareUrl}
        seq={share.currentDeliverySeq}
        onShare={share.handleDeliveryShareComplete}
      />

      <ShareConfirmModal
        open={share.showShareConfirm}
        isSaving={share.isConfirmSaving}
        channel={share.lastShareChannel}
        seq={share.confirmSeq}
        onConfirm={share.handleShareConfirm}
        onDeny={share.handleShareConfirmDeny}
        onLater={share.handleShareConfirmLater}
        onClose={share.handleShareConfirmClose}
      />

      <MemoModal
        open={share.showMemoModal}
        isSaving={share.isMemoSaving}
        onSave={share.handleMemoSave}
        onClose={share.handleMemoClose}
      />
    </div>
  );
};

export default GiftOrderCouponPage;

// ============================================================
// 섹션 컴포넌트 (페이지 전용, 외부에서 재사용하지 않으므로 같은 파일에 둠)
// ============================================================

type CourtesySectionProps = {
  stepNum: number;
  courtesy: ReturnType<typeof useCourtesyEditor>;
};

const CourtesySection: FC<CourtesySectionProps> = ({ stepNum, courtesy }) => (
  <div className="mx-4 mb-4">
    <div className="mb-2 flex items-center gap-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white">
        {stepNum}
      </span>
      <h3 className="text-base font-bold text-gray-800">답례글 확인</h3>
    </div>

    <div className="rounded-xl bg-white shadow-md">
      {!courtesy.isEditing ? (
        <div className="px-4 py-4">
          {courtesy.courtesyContent ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
              {courtesy.courtesyContent}
            </p>
          ) : (
            <p className="text-sm text-gray-400">답례글이 아직 없습니다.</p>
          )}
          <button
            onClick={() => courtesy.setIsEditing(true)}
            className="mt-3 flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 active:bg-purple-100"
          >
            <PencilIcon className="h-4 w-4" />
            답례글 수정
          </button>
        </div>
      ) : (
        <div className="px-4 pb-4 pt-3">
          {courtesy.courtesyContentListStatus === 'success' &&
            courtesy.courtesyContentList &&
            courtesy.courtesyContentList.length > 0 && (
              <div className="mb-3">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  답례글 템플릿
                </label>
                <select
                  value={courtesy.selectedContentIndex}
                  onChange={(e) => courtesy.handleContentSelect(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                >
                  {courtesy.courtesyContentList.map((content, index) => (
                    <option key={content._id} value={index}>
                      {content.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {(courtesy.courtesyContentListStatus === 'loading' ||
            courtesy.courtesyContentListStatus === 'idle') &&
            courtesy.bugoStatus !== 'error' && (
              <div className="mb-3 text-center text-xs text-gray-400">
                답례글 목록을 불러오는 중...
              </div>
            )}

          <textarea
            ref={courtesy.textareaRef}
            value={courtesy.courtesyContent}
            onChange={(e) => {
              courtesy.handleContentChange(e.target.value);
              courtesy.adjustTextareaHeight();
            }}
            placeholder="답례글을 입력하세요..."
            className="min-h-[160px] w-full resize-none overflow-hidden rounded-lg border border-gray-200 p-3 text-sm leading-relaxed focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
          />

          <div className="mt-3 flex gap-2">
            <button
              onClick={courtesy.handleCancelEdit}
              className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 active:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={courtesy.handleSaveCourtesy}
              disabled={courtesy.isSaving || !courtesy.isModified}
              className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition ${
                courtesy.isModified
                  ? 'bg-purple-600 text-white active:bg-purple-700'
                  : 'bg-gray-100 text-gray-400'
              } ${courtesy.isSaving ? 'opacity-50' : ''}`}
            >
              {courtesy.isSaving
                ? '저장 중...'
                : courtesy.isModified
                ? '저장하기'
                : '저장됨'}
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);

type CouponSectionProps = {
  stepNum: number;
  couponList: ReturnType<typeof useGiftOrderShare>['couponList'];
  totalCoupons: number;
  sharedCoupons: number;
  unsharedCoupons: number;
  onShare: ReturnType<typeof useGiftOrderShare>['handleCouponShare'];
  onMemo: ReturnType<typeof useGiftOrderShare>['handleOpenMemoCoupon'];
  onAdditionalPhoneEdit: (couponId: string, phone: string) => Promise<void>;
};

const CouponSection: FC<CouponSectionProps> = function CouponSection({
  stepNum,
  couponList,
  onShare,
  onMemo,
  onAdditionalPhoneEdit,
}) {
  const [showShared, setShowShared] = useState(false);
  const [showAdditional, setShowAdditional] = useState(true);

  const originalCoupons = useMemo(
    () => couponList.filter((c) => !c.isAdditional && !c.isReissued),
    [couponList],
  );
  const carefulCoupons = useMemo(
    () => couponList.filter((c) => c.isAdditional || c.isReissued),
    [couponList],
  );

  const unsharedList = useMemo(
    () =>
      originalCoupons.filter((c) => c.shareStatus === GiftCouponShareStatus.NotShared),
    [originalCoupons],
  );
  const sharedList = useMemo(
    () =>
      originalCoupons.filter((c) => c.shareStatus !== GiftCouponShareStatus.NotShared),
    [originalCoupons],
  );

  const originalTotal = originalCoupons.length;
  const originalShared = sharedList.length;
  const originalUnshared = unsharedList.length;

  return (
    <div className="mx-4 mb-4">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white">
          {stepNum}
        </span>
        <h3 className="text-base font-bold text-gray-800">쿠폰형 답례품 전달</h3>
      </div>

      <ShareProgressCard
        total={originalTotal}
        shared={originalShared}
        unshared={originalUnshared}
      />

      {unsharedList.length > 0 && (
        <>
          <p className="mb-2 text-xs text-gray-500">
            아래 전달하기 버튼을 누르면 위 답례글과 함께 전달됩니다.
          </p>
          <div className="space-y-2">
            {unsharedList.map((coupon) => (
              <CouponRow
                key={coupon._id}
                coupon={coupon}
                onShare={onShare}
                onMemo={onMemo}
                onAdditionalPhoneEdit={onAdditionalPhoneEdit}
                isSharing={false}
              />
            ))}
          </div>
        </>
      )}

      {sharedList.length > 0 && (
        <div className={unsharedList.length > 0 ? 'mt-3' : ''}>
          <button
            onClick={() => setShowShared((prev) => !prev)}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-green-300 bg-green-50 py-2.5 text-sm font-bold text-green-700 active:bg-green-100"
          >
            <CheckCircleSolidIcon className="h-4 w-4" />
            {showShared ? (
              <>
                <ChevronUpIcon className="h-4 w-4" />
                전달완료 {sharedList.length}건 접기
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4" />
                전달완료 {sharedList.length}건 보기
              </>
            )}
          </button>
          {showShared && (
            <div className="mt-2 space-y-2">
              <SharedGuideCard />
              {sharedList.map((coupon) => (
                <CouponRow
                  key={coupon._id}
                  coupon={coupon}
                  onShare={onShare}
                  onMemo={onMemo}
                  onAdditionalPhoneEdit={onAdditionalPhoneEdit}
                  isSharing={false}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* 재발행/재발급 쿠폰 영역 */}
      {carefulCoupons.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowAdditional((prev) => !prev)}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 py-2.5 text-sm font-bold text-amber-700 active:bg-amber-100"
          >
            {showAdditional ? (
              <>
                <ChevronUpIcon className="h-4 w-4" />
                재발행 쿠폰 {carefulCoupons.length}건 접기
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4" />
                재발행 쿠폰 {carefulCoupons.length}건 보기
              </>
            )}
          </button>
          {showAdditional && (
            <>
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-[11px] text-amber-700">
                  <span className="font-bold text-amber-900">
                    전달 전 반드시 받으시는 분의 휴대폰번호를 입력
                  </span>
                  해주세요. 성함은 선택이며, 중복 발송 방지를 위해 번호 입력은 꼭
                  필요합니다.
                </p>
              </div>
              <div className="mt-2 space-y-2">
                {carefulCoupons.map((coupon) => (
                  <CouponRow
                    key={coupon._id}
                    coupon={coupon}
                    onShare={onShare}
                    onMemo={onMemo}
                    onAdditionalPhoneEdit={onAdditionalPhoneEdit}
                    isSharing={false}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

type IndividualDeliverySectionProps = {
  stepNum: number;
  deliveryList: ReturnType<typeof useGiftOrderShare>['deliveryList'];
  totalDeliveries: number;
  sharedDeliveries: number;
  unsharedDeliveries: number;
  onShare: ReturnType<typeof useGiftOrderShare>['handleDeliveryShare'];
  onMemo: ReturnType<typeof useGiftOrderShare>['handleOpenMemoDelivery'];
};

const IndividualDeliverySection: FC<IndividualDeliverySectionProps> =
  function IndividualDeliverySection({
    stepNum,
    deliveryList,
    totalDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    onShare,
    onMemo,
  }) {
    const [showShared, setShowShared] = useState(false);

    const unsharedList = useMemo(
      () =>
        deliveryList.filter((d) => d.shareStatus === GiftDeliveryShareStatus.NotShared),
      [deliveryList],
    );
    const sharedList = useMemo(
      () =>
        deliveryList.filter((d) => d.shareStatus !== GiftDeliveryShareStatus.NotShared),
      [deliveryList],
    );

    return (
      <div className="mx-4 mb-4">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white">
            {stepNum}
          </span>
          <h3 className="text-base font-bold text-gray-800">배송형 답례품 전달</h3>
        </div>

        <ShareProgressCard
          total={totalDeliveries}
          shared={sharedDeliveries}
          unshared={unsharedDeliveries}
        />

        {unsharedList.length > 0 && (
          <>
            <p className="mb-2 text-xs text-gray-500">
              받는 분이 링크를 통해 직접 배송지를 입력합니다.
            </p>
            <div className="space-y-2">
              {unsharedList.map((delivery) => (
                <IndividualDeliveryRow
                  key={delivery._id}
                  delivery={delivery}
                  onShare={onShare}
                  onMemo={onMemo}
                  isSharing={false}
                />
              ))}
            </div>
          </>
        )}

        {sharedList.length > 0 && (
          <div className={unsharedList.length > 0 ? 'mt-3' : ''}>
            <button
              onClick={() => setShowShared((prev) => !prev)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-green-300 bg-green-50 py-2.5 text-sm font-bold text-green-700 active:bg-green-100"
            >
              <CheckCircleSolidIcon className="h-4 w-4" />
              {showShared ? (
                <>
                  <ChevronUpIcon className="h-4 w-4" />
                  전달완료 {sharedList.length}건 접기
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-4 w-4" />
                  전달완료 {sharedList.length}건 보기
                </>
              )}
            </button>
            {showShared && (
              <div className="mt-2 space-y-2">
                <SharedGuideCard />
                {sharedList.map((delivery) => (
                  <IndividualDeliveryRow
                    key={delivery._id}
                    delivery={delivery}
                    onShare={onShare}
                    onMemo={onMemo}
                    isSharing={false}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

const SharedGuideCard: FC = () => (
  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3">
    <div className="mb-1.5 flex items-center gap-1.5">
      <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
      <p className="text-xs font-bold text-blue-800">
        전달 안하신 쿠폰이 전달완료로 뜨나요?
      </p>
    </div>
    <p className="mb-2 text-[11px] leading-relaxed text-blue-700">
      공유 화면으로 넘어가시면 이후 과정은 고객님 기기에서 직접 이루어지며,{' '}
      <strong>정보통신망법에 따른 통신 보안 정책상</strong>
      저희가 전달 결과를 확인할 수 없어요. 그래서 공유 화면으로 넘어가신 시점에
      &apos;전달완료&apos;로 처리됩니다.
    </p>
    <ul className="space-y-1 text-[11px] leading-relaxed text-blue-700">
      <li>
        • 전달이 안 됐다면{' '}
        <span className="font-bold text-blue-900">&quot;다시 전달&quot;</span> 버튼을
        눌러주세요
      </li>
      <li>
        • <span className="font-bold text-blue-900">#순번</span>으로 전달 순서를 확인할 수
        있어요
      </li>
      <li>
        • 어떤 분께 전달하셨는지 <span className="font-bold text-blue-900">메모</span>를
        남겨두시면 나중에 헷갈리지 않아요
      </li>
    </ul>
  </div>
);

type ShareProgressCardProps = {
  total: number;
  shared: number;
  unshared: number;
};

const ShareProgressCard: FC<ShareProgressCardProps> = ({ total, shared, unshared }) => (
  <div className="mb-3 rounded-xl bg-white p-3 shadow-md">
    <div className="flex items-center justify-center gap-6">
      <div className="text-center">
        <p className="text-xl font-bold text-purple-600">{total}</p>
        <p className="text-xs text-gray-500">전체</p>
      </div>
      <div className="text-center">
        <p className="text-xl font-bold text-green-600">{shared}</p>
        <p className="text-xs text-gray-500">전달</p>
      </div>
      <div className="text-center">
        <p className="text-xl font-bold text-gray-400">{unshared}</p>
        <p className="text-xs text-gray-500">미전달</p>
      </div>
    </div>

    {unshared === 0 && total > 0 && (
      <div className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-green-100 py-2.5 text-green-700">
        <CheckCircleSolidIcon className="h-5 w-5" />
        <span className="font-bold">모두 전달 완료!</span>
      </div>
    )}
  </div>
);
